import { useParams } from 'react-router-dom';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { toast } from 'react-toastify';
import { StatusCodes } from 'http-status-codes';
import { getErrorMessage, getErrorStatus } from '@common/utils';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '../ui/button';
import { useMutatePublishProject } from '@/reactQuery/post';
import useTranslation from '@/hooks/useTranslation';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { ECompanyThemeName, EPublishErrorMessage } from '@/enums';

interface PublishProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectTitle?: string;
  projectId?: string;
  isPublishRequestApproved: boolean;
}

function PublishProjectModal({
  isOpen,
  onClose,
  projectTitle,
  projectId,
  isPublishRequestApproved,
}: PublishProjectModalProps) {
  const { organization_id: organizationId, workspace_id: workspaceId } = useParams();
  const { lang } = useTranslation();
  // TELV1-268 — respondent limit feeds the publish-time cadence disclosure.
  const { currentProject } = useCurrentProject();
  const { themeName } = useTheme();

  const { mutateAsync: mutateAsyncPublishProject, isPending } = useMutatePublishProject(
    organizationId,
    workspaceId,
  );

  const handlePublishProject = async (formData?: string) => {
    try {
      await mutateAsyncPublishProject(formData);
      toast.success(
        `${lang.get('msg.project')} "${projectTitle}" ${lang.get('msg.isSuccessfullyPublished')}`,
      );
      onClose();
    } catch (error) {
      // These axios instances reject with the unwrapped response, so read via
      // the getError* helpers (never error.response.X) — see getErrorDetails.ts.
      const status = getErrorStatus(error);
      const backendMessage = getErrorMessage(error);
      if (
        status === StatusCodes.FORBIDDEN &&
        backendMessage === EPublishErrorMessage.NOT_SUBSCRIBED
      ) {
        // Only the genuine "not subscribed" 403 should send users to sales.
        toast.error(
          themeName === ECompanyThemeName.EMPATHY
            ? lang.get('msg.projectSubscriptionPublishError_empathy')
            : lang.get('msg.projectSubscriptionPublishError'),
        );
      } else if (backendMessage === EPublishErrorMessage.PROJECT_NOT_READY) {
        // The project failed publish-readiness validation, not a billing issue.
        toast.error(lang.get('msg.someOfRequiredFieldsAreEmpty'));
      } else {
        toast.error(lang.get('msg.projectIsNotPublishedPleaseTryAgain'));
      }
    }
  };

  const isEmpathyTheme = themeName === ECompanyThemeName.EMPATHY;

  return (
    <AlertDialog open={isOpen}>
      {isOpen && (
        <AlertDialogContent className="max-w-lg max-h-[90vh] overflow-y-auto flex flex-col gap-14">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex flex-wrap items-center gap-2">
              {lang.get('msg.publish')} {isEmpathyTheme ? 'project' : 'Tellet'}
            </AlertDialogTitle>
            <p className="text-sm text-dark-text">
              {isEmpathyTheme
                ? lang.get('msg.areYouSureYouWantToPublishThisTellet_empathy')
                : lang.get('msg.areYouSureYouWantToPublishThisTellet')}
              <span className="font-semibold">{` - ${projectTitle}?`}</span>
            </p>
            {/* TELV1-268 — publish-time cadence disclosure: set the
                re-run expectation BEFORE fielding starts. Mirrors the
                server's step formula (5% of the respondent limit,
                floor 3; fixed 20 when unlimited); the authoritative
                number always comes from the Results page disclosure. */}
            {(() => {
              const max = Number(currentProject?.respondents_max ?? 0);
              const step = max > 0 ? Math.min(Math.max(Math.ceil(max * 0.05), 3), max) : 20;
              return (
                <p className="text-xs text-neutral-500">
                  {`While the project is live, analysis re-runs become available every ~${step} new completed interviews (5% of your respondent limit). Analysis always runs when you complete the project.`}
                </p>
              );
            })()}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={onClose}>{lang.get('msg.cancel')}</AlertDialogCancel>
            <Button
              variant="default"
              onClick={() => {
                handlePublishProject(projectId);
              }}
              disabled={isPending}
              type="button"
            >
              {isPublishRequestApproved ? lang.get('msg.requestApproved') : lang.get('msg.publish')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      )}
    </AlertDialog>
  );
}

export default PublishProjectModal;
